import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import { useRef, useState } from 'react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { TableKit } from '@tiptap/extension-table'
import { Details, DetailsContent, DetailsSummary } from '@tiptap/extension-details'
import { FileHandler } from '@tiptap/extension-file-handler'
import { TextAlign } from '@tiptap/extension-text-align'
import { cn } from '@/lib/utils'
import { ArticleImage } from '@/components/help_center/editor/ArticleImage'
import { ArticleVideo } from '@/components/help_center/editor/ArticleVideo'
import { ArticleLoom } from '@/components/help_center/editor/ArticleLoom'
import { TableOfContents } from '@/components/help_center/editor/TableOfContents'
import { EditorToolbar } from '@/components/help_center/editor/EditorToolbar'
import { EditorDialogs } from '@/components/help_center/editor/EditorDialogs'
import { SlashCommand } from '@/components/help_center/editor/slashCommand'
import { buildSlashItems } from '@/components/help_center/editor/slashItems'
import { expandPastedUrl } from '@/components/help_center/editor/linkProviders'
import type { DialogKind } from '@/components/help_center/editor/dialogKinds'
import type { LinkableArticle } from '@/components/help_center/ArticleLinkDialog'
import { processImage, uploadImage } from '@/lib/imageProcessing'
import { RICH_PROSE_BASE } from '@/lib/richProseClasses'

export interface ArticleContent {
  json: Record<string, unknown>
  html: string
}

interface ArticleContentEditorProps {
  initialContent: Record<string, unknown> | null
  /** HTML fallback for articles authored outside the editor (e.g. via MCP),
   *  which have rendered_html but no content_json. TipTap parses it natively. */
  fallbackHtml?: string | null
  articles?: LinkableArticle[]
  accountSlug?: string
  /** Fired once the editor has parsed its initial content — gives the page the
   *  canonical baseline JSON for dirty tracking (normalized by TipTap). */
  onReady?: (json: Record<string, unknown>) => void
  onChange: (value: ArticleContent) => void
}

const IMAGE_MIME_TYPES = [ 'image/png', 'image/jpeg', 'image/gif', 'image/webp' ]

const PROSE_EXTRAS =
  '[&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1.5 ' +
  '[&_pre]:bg-card [&_pre]:rounded-md [&_pre]:p-3 [&_pre]:text-sm [&_pre]:overflow-x-auto [&_hr]:my-4 [&_hr]:border-border ' +
  '[&_table]:w-full [&_table]:border-collapse [&_table]:my-3 [&_th]:border [&_th]:border-border [&_td]:border [&_td]:border-border ' +
  '[&_th]:px-2.5 [&_th]:py-1.5 [&_td]:px-2.5 [&_td]:py-1.5 [&_th]:bg-muted [&_th]:font-semibold [&_th]:text-left ' +
  '[&_iframe]:my-3 [&_iframe]:max-w-full [&_iframe]:rounded-md ' +
  // Details (collapsible) blocks are styled in global.css — the extension
  // renders div[data-type=details], not a native <details>.
  "[&_.is-empty]:before:content-[attr(data-placeholder)] [&_.is-empty]:before:text-muted-foreground [&_.is-empty]:before:float-left [&_.is-empty]:before:h-0"

async function insertProcessedImage(editor: Editor, file: File, pos?: number) {
  const processed = await processImage(file, null)
  const src = await uploadImage(processed)
  // Uncropped, so the upload IS the original — re-crop works from it later.
  const node = { type: 'image', attrs: { src, alt: '', width: processed.width, originalSrc: src } }
  if (pos !== undefined) {
    editor.chain().focus().insertContentAt(pos, node).run()
  } else {
    editor.chain().focus().insertContent(node).run()
  }
}

export function ArticleContentEditor({ initialContent, fallbackHtml, articles = [], accountSlug = '', onReady, onChange }: ArticleContentEditorProps) {
  const [openDialog, setOpenDialog] = useState<DialogKind | null>(null)
  // Lets editorProps callbacks reach the editor without referencing the
  // still-initializing `editor` const (TS circular-inference).
  const editorRef = useRef<Editor | null>(null)
  const editor = useEditor({
    extensions: [
      StarterKit,
      // inclusive: false so typing right after a link/chip doesn't extend the
      // link mark into the new text (it's a mark-spec prop, set via extend).
      Link.extend({ inclusive: false }).configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: 'nofollow noopener', target: '_blank' } }),
      Placeholder.configure({ placeholder: "Write your article — press '/' for commands…" }),
      TextAlign.configure({ types: [ 'heading', 'paragraph' ] }),
      ArticleImage.configure({ HTMLAttributes: { class: 'rounded-lg max-w-full' } }),
      TableKit.configure({ table: { resizable: false } }),
      Details, DetailsSummary, DetailsContent,
      // Paste-to-embed is centralized in linkProviders (editorProps.handlePaste);
      // disable the extension's own handler so it isn't double-handled.
      ArticleVideo.configure({ nocookie: true, width: 640, height: 360, addPasteHandler: false }),
      // Loom paste-to-embed is centralized in linkProviders (handlePaste), same
      // as YouTube — the node itself has no paste handler.
      ArticleLoom,
      TableOfContents,
      SlashCommand.configure({ commandItems: buildSlashItems(setOpenDialog) }),
      FileHandler.configure({
        allowedMimeTypes: IMAGE_MIME_TYPES,
        onDrop: (currentEditor, files, pos) => {
          files.forEach(file => { void insertProcessedImage(currentEditor as Editor, file, pos) })
        },
        onPaste: (currentEditor, files) => {
          files.forEach(file => { void insertProcessedImage(currentEditor as Editor, file) })
        },
      }),
    ],
    content: initialContent && Object.keys(initialContent).length ? initialContent : (fallbackHtml ?? ''),
    editorProps: {
      attributes: { class: cn('min-h-[420px] px-4 py-3 text-sm text-foreground focus:outline-none', RICH_PROSE_BASE, PROSE_EXTRAS) },
      // Never follow links while editing — openOnClick: false stops TipTap's
      // handler, but the browser can still navigate natively (e.g. first
      // click into an unfocused editor). Edit links via the Link dialog.
      // Smart paste: a bare provider URL (e.g. YouTube) auto-expands to an
      // embed. Only when not pasting over a selection (that's "make a link").
      handlePaste: (_view, event) => {
        const current = editorRef.current
        if (!current || !current.state.selection.empty) return false
        const text = event.clipboardData?.getData('text/plain')
        return text ? expandPastedUrl(current, text) : false
      },
      handleDOMEvents: {
        click: (_view, event) => {
          const target = event.target as HTMLElement
          // Clicking an image selects it (atom node) — open the edit dialog.
          if (target.tagName === 'IMG') {
            setOpenDialog('image')
            return false
          }
          const anchor = target.closest('a')
          if (anchor) {
            // Only block links that would navigate AWAY from the editor. In-page
            // anchors (#…, e.g. a table-of-contents link) keep you on the page,
            // so let them work.
            const href = anchor.getAttribute('href') ?? ''
            if (!href.startsWith('#')) event.preventDefault()
            return false
          }
          return false
        },
      },
    },
    // Toolbar active states + table controls depend on selection.
    shouldRerenderOnTransaction: true,
    onCreate: ({ editor }) => {
      editorRef.current = editor
      onReady?.(editor.getJSON() as Record<string, unknown>)
    },
    onUpdate: ({ editor }) => {
      onChange({ json: editor.getJSON() as Record<string, unknown>, html: editor.isEmpty ? '' : editor.getHTML() })
    },
  })

  return (
    <div className="rounded-lg border border-border">
      <EditorToolbar editor={editor} onOpenDialog={setOpenDialog} />
      <EditorContent editor={editor} />
      {editor && (
        <EditorDialogs
          editor={editor}
          open={openDialog}
          onClose={() => setOpenDialog(null)}
          articles={articles}
          accountSlug={accountSlug}
        />
      )}
    </div>
  )
}
